import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { toast } from "sonner";
import { trackEvent } from "@/lib/analytics";

export type BookStatus = "read" | "tbr" | "reading" | "skipped";

export interface UserBook {
  id: string;
  book_id: string;
  status: BookStatus;
}

export const useUserBooks = () => {
  const { user } = useAuth();
  const [items, setItems] = useState<UserBook[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user) {
      setItems([]);
      setLoading(false);
      return;
    }
    const { data, error } = await supabase
      .from("user_books")
      .select("id, book_id, status")
      .eq("user_id", user.id);
    if (!error && data) setItems(data as UserBook[]);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const setStatus = async (bookId: string, status: BookStatus) => {
    if (!user) return toast.error("Sign in to track your books");
    const { error } = await supabase
      .from("user_books")
      .upsert(
        { user_id: user.id, book_id: bookId, status },
        { onConflict: "user_id,book_id" }
      );
    if (error) {
      toast.error("Could not update");
      return;
    }
    const labels: Record<BookStatus, string> = {
      read: "Marked as read",
      tbr: "Saved to TBR",
      reading: "Now reading",
      skipped: "Skipped — we won't recommend this again",
    };
    toast.success(labels[status]);
    if (status === "tbr") trackEvent("book_tbr", { book_id: bookId });
    if (status === "read") trackEvent("book_read", { book_id: bookId });
    if (status === "skipped") trackEvent("book_skipped", { book_id: bookId });
    refresh();
  };

  const remove = async (bookId: string) => {
    if (!user) return;
    await supabase.from("user_books").delete().eq("user_id", user.id).eq("book_id", bookId);
    refresh();
  };

  const statusFor = (bookId: string): BookStatus | null =>
    items.find((i) => i.book_id === bookId)?.status ?? null;

  const readBookIds = new Set(items.filter((i) => i.status === "read").map((i) => i.book_id));
  const skippedBookIds = new Set(items.filter((i) => i.status === "skipped").map((i) => i.book_id));
  // Books to exclude from new recommendations (read + skipped).
  const excludedBookIds = new Set([...readBookIds, ...skippedBookIds]);

  return { items, loading, setStatus, remove, statusFor, readBookIds, skippedBookIds, excludedBookIds, refresh };
};
